import { Search, X, Building2, Tag } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

interface KanbanFiltersProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  cityFilter: string;
  setCityFilter: (city: string) => void;
  orgFilter: string;
  setOrgFilter: (org: string) => void;
  tagFilter?: string;
  setTagFilter?: (tag: string) => void;
  cities: string[];
  organizations: Array<{ id: string; name: string }>;
  availableTags?: string[];
  extraFilters?: React.ReactNode;
}

const ALL_CITIES = "__ALL_CITIES__";
const ALL_ORGS = "__ALL_ORGS__";
const ALL_TAGS = "__ALL_TAGS__";

export function KanbanFilters({
  searchQuery,
  setSearchQuery,
  cityFilter,
  setCityFilter,
  orgFilter,
  setOrgFilter,
  tagFilter = '',
  setTagFilter,
  cities = [],
  organizations = [],
  availableTags = [],
  extraFilters,
}: KanbanFiltersProps) {
  const activeFiltersCount = [
    searchQuery,
    cityFilter,
    orgFilter,
    tagFilter,
  ].filter(Boolean).length;

  const handleReset = () => {
    setSearchQuery("");
    setCityFilter("");
    setOrgFilter("");
    if (setTagFilter) setTagFilter("");
  };

  return (
    <Card className="p-4 mb-6">
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Zoeken op naam, organisatie..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* City Filter */}
          <Select value={cityFilter} onValueChange={(v) => setCityFilter(v === ALL_CITIES ? "" : v)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Alle steden" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_CITIES}>Alle steden</SelectItem>
              {cities.map((city) => (
                <SelectItem key={city} value={city}>
                  {city}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Organization Filter */}
          <Select value={orgFilter} onValueChange={(v) => setOrgFilter(v === ALL_ORGS ? "" : v)}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Alle organisaties" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_ORGS}>Alle organisaties</SelectItem>
              {organizations.map((org) => (
                <SelectItem key={org.id} value={org.id}>
                  <div className="flex items-center gap-2">
                    <Building2 className="h-3 w-3" />
                    {org.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Tag Filter */}
          {setTagFilter && availableTags.length > 0 && (
            <Select value={tagFilter} onValueChange={(v) => setTagFilter(v === ALL_TAGS ? "" : v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Alle tags" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_TAGS}>Alle tags</SelectItem>
                {availableTags.map((tag) => (
                  <SelectItem key={tag} value={tag}>
                    <div className="flex items-center gap-2">
                      <Tag className="h-3 w-3" />
                      {tag}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Reset Button */}
          {activeFiltersCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="gap-2"
            >
              <X className="h-4 w-4" />
              Reset
            </Button>
          )}
        </div>

        {/* Extra Filters */}
        {extraFilters && (
          <div className="flex items-center gap-4">
            {extraFilters}
          </div>
        )}

        {/* Active Filter Badges */}
        {activeFiltersCount > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-muted-foreground">Actieve filters:</span>
            {searchQuery && (
              <Badge variant="secondary" className="gap-1">
                Zoeken: "{searchQuery}"
                <X
                  className="h-3 w-3 cursor-pointer"
                  onClick={() => setSearchQuery("")}
                />
              </Badge>
            )}
            {cityFilter && (
              <Badge variant="secondary" className="gap-1">
                Stad: {cityFilter}
                <X
                  className="h-3 w-3 cursor-pointer"
                  onClick={() => setCityFilter("")}
                />
              </Badge>
            )}
            {orgFilter && (
              <Badge variant="secondary" className="gap-1">
                Organisatie: {organizations?.find(o => o.id === orgFilter)?.name || orgFilter}
                <X
                  className="h-3 w-3 cursor-pointer"
                  onClick={() => setOrgFilter("")}
                />
              </Badge>
            )}
            {tagFilter && setTagFilter && (
              <Badge variant="secondary" className="gap-1">
                Tag: {tagFilter}
                <X
                  className="h-3 w-3 cursor-pointer"
                  onClick={() => setTagFilter("")}
                />
              </Badge>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
